import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  CLIENT_RELEASE_MAX_BYTES,
  type ClientReleaseAdminView,
  type ClientReleaseInput,
  type ClientReleaseUploadView,
  type ClientReleaseView,
} from 'shared';
import { adminApi, onAdminTokenChange } from '@/api';

interface UploadState {
  id: string;
  file: File | null;
  size: number;
  offset: number;
  phase: 'uploading' | 'failed' | 'completed' | 'cancelled';
  error: string;
}
const endpoint = '/admin/client-releases';

export const useClientReleasesStore = defineStore('client-releases', () => {
  const items = ref<ClientReleaseAdminView[]>([]);
  const loading = ref(false);
  const error = ref('');
  const target = ref('');
  const channel = ref('');
  const status = ref('');
  const uploads = ref<Record<string, UploadState>>({});
  const visibleItems = computed(() =>
    items.value.filter(
      (item) =>
        (!target.value || item.target === target.value) &&
        (!channel.value || item.channel === channel.value) &&
        (!status.value || item.status === status.value),
    ),
  );
  let epoch = 0;
  let request = 0;

  async function load() {
    const current = ++request;
    loading.value = true;
    error.value = '';
    try {
      const result = await adminApi.get<ClientReleaseAdminView[]>(endpoint);
      if (current === request) {
        items.value = result;
        for (const item of result)
          if (item.upload && !uploads.value[item.id]?.id) {
            uploads.value[item.id] = {
              id: item.upload.id,
              file: uploads.value[item.id]?.file ?? null,
              size: item.upload.size,
              offset: item.upload.offset,
              phase: 'failed',
              error: '检测到未完成上传，可重试或取消后重新选择安装包。',
            };
          }
      }
    } catch (cause) {
      if (current === request) error.value = (cause as Error).message;
    } finally {
      if (current === request) loading.value = false;
    }
  }

  async function save(input: ClientReleaseInput, reason: string, existing?: ClientReleaseView) {
    const version = epoch;
    const saved = existing
      ? await adminApi.patch<ClientReleaseView>(`${endpoint}/${existing.id}`, {
          ...input,
          reason,
          revision: existing.revision,
        })
      : await adminApi.post<ClientReleaseView>(endpoint, { ...input, reason });
    if (version !== epoch) throw new Error('管理员会话已变更');
    await load();
    return saved;
  }

  async function act(
    item: ClientReleaseView,
    action: 'publish' | 'set-current' | 'archive' | 'delete',
    reason: string,
  ) {
    const body = { revision: item.revision, reason };
    if (action === 'delete') await adminApi.delete(`${endpoint}/${item.id}`, body);
    else await adminApi.post(`${endpoint}/${item.id}/${action}`, body);
    await load();
  }

  async function upload(item: ClientReleaseView, file: File) {
    if (!file.size || file.size > CLIENT_RELEASE_MAX_BYTES)
      throw new Error('安装包大小必须在 1 B 到 1 GiB 之间');
    if (uploads.value[item.id]?.phase === 'uploading') throw new Error('该版本正在上传');
    const version = epoch;
    uploads.value[item.id] = {
      id: '',
      file,
      size: file.size,
      offset: 0,
      phase: 'uploading',
      error: '',
    };
    try {
      const session = await adminApi.post<ClientReleaseUploadView>(
        `${endpoint}/${item.id}/uploads`,
        { name: file.name, size: file.size, revision: item.revision },
      );
      if (version !== epoch) return;
      uploads.value[item.id]!.id = session.id;
      await transfer(item.id, session, version);
    } catch (cause) {
      fail(item.id, cause, version);
    } finally {
      if (version === epoch) await load();
    }
  }

  async function retry(releaseId: string) {
    const state = uploads.value[releaseId];
    if (!state || state.phase !== 'failed' || !state.id) return;
    const version = epoch;
    state.phase = 'uploading';
    state.error = '';
    try {
      const session = await adminApi.get<ClientReleaseUploadView & { status: string }>(
        `${endpoint}/uploads/${state.id}`,
      );
      if (version !== epoch) return;
      if (session.status === 'completed') state.phase = 'completed';
      else if (session.status === 'completing') await finish(releaseId, version);
      else if (session.status === 'active') await transfer(releaseId, session, version);
      else throw new Error('上传已取消或过期，请重新选择安装包');
    } catch (cause) {
      fail(releaseId, cause, version);
    } finally {
      if (version === epoch) await load();
    }
  }

  async function transfer(releaseId: string, session: ClientReleaseUploadView, version: number) {
    const state = uploads.value[releaseId]!;
    const file = state.file;
    if (!file) throw new Error('页面已刷新，请取消旧上传后重新选择安装包');
    let offset = session.offset;
    if (
      !Number.isSafeInteger(session.chunkBytes) ||
      session.chunkBytes < 1 ||
      !Number.isSafeInteger(offset) ||
      offset < 0 ||
      offset > file.size
    )
      throw new Error('服务端上传进度无效');
    while (offset < file.size && version === epoch && state.phase === 'uploading') {
      const body = new FormData();
      body.append('offset', String(offset));
      body.append('chunk', file.slice(offset, offset + session.chunkBytes));
      const next = await adminApi.patch<{ offset: number }>(
        `${endpoint}/uploads/${session.id}`,
        body,
      );
      if (!Number.isSafeInteger(next.offset) || next.offset <= offset || next.offset > file.size)
        throw new Error('上传进度异常，请重试');
      offset = next.offset;
      state.offset = offset;
    }
    if (version === epoch && state.phase === 'uploading') await finish(releaseId, version);
  }

  async function finish(releaseId: string, version: number) {
    const state = uploads.value[releaseId]!;
    await adminApi.post(`${endpoint}/uploads/${state.id}/complete`, {});
    if (version === epoch && state.phase === 'uploading') state.phase = 'completed';
  }

  function fail(releaseId: string, cause: unknown, version: number) {
    const state = uploads.value[releaseId];
    if (version !== epoch || !state || state.phase === 'cancelled') return;
    state.phase = 'failed';
    state.error = (cause as Error).message || '上传失败，请重试';
  }

  async function cancel(releaseId: string) {
    const state = uploads.value[releaseId];
    if (!state?.id) return;
    await adminApi.delete(`${endpoint}/uploads/${state.id}`);
    state.phase = 'cancelled';
    await load();
  }

  onAdminTokenChange((token) => {
    if (token) return;
    epoch++;
    request++;
    items.value = [];
    uploads.value = {};
    loading.value = false;
    error.value = '';
    target.value = channel.value = status.value = '';
  });
  return {
    items,
    visibleItems,
    loading,
    error,
    target,
    channel,
    status,
    uploads,
    load,
    save,
    act,
    upload,
    retry,
    cancel,
  };
});
